import {
  type BankOperation,
  type BankOperationsFilter,
  useBankOperationsQuery,
} from '../../../../entities/bank-transaction/index.js';
import { formatStoredDateForPeople } from '../../../../features/payment-view/index.js';
import { formatApproximate, formatSignedMoney } from '../../../../features/summary-view/index.js';
import { Button, ErrorState, List, ListItem, Spinner } from '../../../../shared/ui/index.js';
import {
  convertedAmountMinorOf,
  operationDescriptionOf,
  operationNoteOf,
} from '../../lib/operation-text.js';
import styles from './operation-list.module.css';

type OperationListProps = {
  filter: BankOperationsFilter;
  baseCurrency: string;
  onOpen: (operationId: string) => void;
};

const convertedAmountTextOf = (
  operation: BankOperation,
  baseCurrency: string,
): string | undefined => {
  const convertedMinor = convertedAmountMinorOf(operation, baseCurrency);

  return convertedMinor === null
    ? undefined
    : formatApproximate(formatSignedMoney(convertedMinor, baseCurrency));
};

const emptyTextOf = (filter: BankOperationsFilter): string =>
  filter.month === null ? 'За цей рік операцій немає' : 'За цей місяць операцій немає';

export const OperationList = ({ filter, baseCurrency, onOpen }: OperationListProps) => {
  const operations = useBankOperationsQuery(filter);

  if (operations.isPending) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  if (operations.isLoadingError) {
    return (
      <ErrorState
        title="Не вдалося завантажити операції"
        message={operations.error.message}
        onRetry={() => {
          void operations.refetch();
        }}
      />
    );
  }

  const rows = operations.data.pages.flatMap((page) => page.operations);
  const canLoadMore = operations.hasNextPage && !operations.isFetchNextPageError;

  const loadMore = (): void => {
    void operations.fetchNextPage();
  };

  return (
    <>
      <List header="Операції">
        {rows.length === 0 && <ListItem title={emptyTextOf(filter)} />}
        {rows.map((operation) => (
          <ListItem
            key={operation.id}
            title={operationDescriptionOf(operation)}
            subtitle={formatStoredDateForPeople(operation.occurredOn)}
            note={operationNoteOf(operation)}
            isSecondLineWrapping
            value={formatSignedMoney(operation.amountMinor, operation.currency)}
            valueNote={convertedAmountTextOf(operation, baseCurrency)}
            chevron
            onClick={() => {
              onOpen(operation.id);
            }}
          />
        ))}
      </List>

      {operations.isFetchNextPageError && (
        <ErrorState
          title="Не вдалося завантажити ще"
          message={operations.error.message}
          onRetry={loadMore}
        />
      )}
      {canLoadMore && (
        <Button variant="secondary" busy={operations.isFetchingNextPage} onClick={loadMore}>
          Показати ще
        </Button>
      )}
    </>
  );
};
